import React, { FC, useEffect } from 'react';

import type { ThemeKey } from '../../types';
import { getActions, getGlobal, withGlobal } from '../../global';

import useFlag from '../../hooks/useFlag';

import {
  selectIsRightColumnShown,
  selectTabState,
  selectTheme,
} from '../../global/selectors';
import { GlobalState, TabState } from '../../global/types';
import classNames from 'classnames';
import { Notifications } from '../../bundles/extra';

import styles from './GlobalLayout.module.scss';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import { useTranslation } from 'react-i18next';
import useBrowserOnline from '../../hooks/useBrowserOnline';
import { Outlet } from 'react-router';
import * as mediaLoader from '../../util/mediaLoader';
import { updateSizes } from '../../util/windowSize';
import { addActiveTabChangeListener } from '../../util/activeTabMonitor';
import { IS_MULTITAB_SUPPORTED } from '../../util/windowEnvironment';
import AppInactive from '../../components/main/AppInactive';
import { DEBUG } from '../../config';
import ComingSoonPage from '../../components/main/ComingSoonPage';
import { getChatAvatarHash } from '../../global/helpers';
import {
  ApiMediaFormat,
  ApiUpdateAuthorizationStateType,
} from '../../api/types';
import { Bundles, loadModule } from '../../util/moduleLoader';
import preloadFonts from '../../util/fonts';
import { pause } from '../../util/schedulers';
import PreLoader from '../../components/common/PreLoader';

export type UiLoaderPage =
  | 'main'
  | 'lock'
  | 'inactive'
  | 'authCode'
  | 'authPassword'
  | 'authPhoneNumber'
  | 'authQrCode'
  | 'authResetPassword'
  | 'authRegister'
  | 'authConfirm'
  | 'infoAccount'
  | 'blockedUser';

const MAX_PRELOAD_DELAY = 300;
const SECOND_STATE_DELAY = 500;
const AVATARS_TO_PRELOAD = 10;

function preloadAvatars() {
  const { listIds, byId } = getGlobal().chats;
  if (!listIds.active) {
    return undefined;
  }

  return Promise.all(
    listIds.active.slice(0, AVATARS_TO_PRELOAD).map((chatId) => {
      const chat = byId[chatId];
      if (!chat) {
        return undefined;
      }

      const avatarHash = getChatAvatarHash(chat);
      if (!avatarHash) {
        return undefined;
      }

      return mediaLoader.fetch(avatarHash, ApiMediaFormat.BlobUrl);
    })
  );
}

const preloadTasks = {
  main: () =>
    Promise.all([
      loadModule(Bundles.Main).then(preloadFonts),
      preloadAvatars(),
    ]),
};

type StateProps = Pick<
  TabState,
  'uiReadyState' | 'shouldSkipHistoryAnimations'
> & {
  isRightColumnShown?: boolean;
  leftColumnWidth?: number;
  theme: ThemeKey;
  hasNotifications: boolean;
  authState?: ApiUpdateAuthorizationStateType;
  connectionState: GlobalState['connectionState'];
  isInactiveAuth?: boolean;
  lastSyncTime?: number;
};

const GlobalLayout: FC<StateProps> = ({
  connectionState,
  hasNotifications,
  isInactiveAuth,
  lastSyncTime,
  authState,
}) => {
  const { disconnect, setIsUiReady } = getActions();
  const { t } = useTranslation();
  const [isInactive, markInactive, unmarkInactive] = useFlag();
  const [isReady, markReady] = useFlag();
  const [isDisconnectModalOpen, openDisconnectModal, closeDisconnectModal] =
    useFlag();
  const isBrowserOnline = useBrowserOnline();

  useEffect(() => {
    let timeout: number | undefined;
    if (authState !== 'authorizationStateReady') {
      markReady();
    }
    if (!lastSyncTime) return;
    const safePreload = async () => {
      try {
        await preloadTasks.main();
      } catch (err) {
        // Do nothing
      }
    };

    Promise.race([pause(MAX_PRELOAD_DELAY), safePreload()]).then(() => {
      setIsUiReady({ uiReadyState: 1 });
      timeout = window.setTimeout(() => {
        markReady();
        setIsUiReady({ uiReadyState: 2 });
      }, SECOND_STATE_DELAY);
    });

    return () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = undefined;
      }

      setIsUiReady({ uiReadyState: 0 });
    };
  }, [lastSyncTime]);

  useEffect(() => {
    let timeout;
    if (connectionState === 'connectionStateConnecting') {
      timeout = setTimeout(() => {
        openDisconnectModal();
      }, 6000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [connectionState]);

  useEffect(() => {
    if (isInactiveAuth) {
      markInactive();
    } else {
      unmarkInactive();
    }
  }, [isInactiveAuth, markInactive, unmarkInactive]);

  useEffect(() => {
    updateSizes();

    if (IS_MULTITAB_SUPPORTED) return;

    addActiveTabChangeListener(() => {
      disconnect();
      markInactive();
    });
  }, [disconnect, markInactive]);

  return (
    <>
      {!isReady && <PreLoader />}
      <div id='portals'></div>
      <div id='UiLoader' className={styles.bg}>
        <div className={classNames(styles.main)}>
          {!DEBUG ? (
            <ComingSoonPage />
          ) : isInactive ? (
            <AppInactive />
          ) : (
            <Outlet />
          )}
        </div>
      </div>
      {isBrowserOnline && connectionState === 'connectionStateConnecting' && (
        <Modal
          isOpen={isDisconnectModalOpen}
          onClose={closeDisconnectModal}
          className='error'
          title='System overloaded!'
        >
          <div className='modal-content'>
            {t('Errors.client_disconnect')}
            <div className='dialog-buttons mt-2'>
              <Button isText onClick={closeDisconnectModal}>
                {t('OK')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <Notifications isOpen={hasNotifications} />
    </>
  );
};

export default withGlobal((global, { isMobile }): StateProps => {
  const theme = selectTheme(global);
  const { connectionState, authState, lastSyncTime } = global;
  const {
    shouldSkipHistoryAnimations,
    uiReadyState,
    notifications,
    isInactive,
  } = selectTabState(global);

  return {
    shouldSkipHistoryAnimations: shouldSkipHistoryAnimations,
    uiReadyState: uiReadyState,
    isRightColumnShown: selectIsRightColumnShown(global, isMobile),
    leftColumnWidth: global.leftColumnWidth,
    hasNotifications: Boolean(notifications.length),
    theme,
    authState,
    connectionState,
    isInactiveAuth: isInactive && authState !== 'authorizationStateReady',
    lastSyncTime,
  };
})(GlobalLayout);
